import { useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../../api/repository'
import type { CategoryNode, Collection } from '../../model/types'
import {
  Button,
  EmptyState,
  Panel,
  ScreenHeader,
  Section
} from '../../ui/primitives'
import { TextField } from '../../ui/fields'
import { useRemote } from '../../state/useRemote'
import styles from './catalog.module.css'

/**
 * Каталог коллекций: поиск, категории, страничная выдача.
 *
 * Фильтры живут в адресной строке, а не в состоянии компонента: ссылку
 * на «российский футбол, вторая страница» должно быть можно отправить,
 * а кнопка «назад» в браузере обязана возвращать к прошлой выборке, а
 * не к пустому каталогу.
 */
export function CollectionsScreen(): ReactNode {
  const [params, setParams] = useSearchParams()

  const category = params.get('category')
  const query = params.get('query') ?? ''
  const page = Number(params.get('page') ?? '1')

  const categories = useRemote(() => api.categories(), [])
  const collections = useRemote(
    () => api.collections(params),
    [params.toString()]
  )

  /** Правка любого фильтра возвращает на первую страницу. */
  function apply(patch: Record<string, string | null>): void {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value.length === 0) next.delete(key)
      else next.set(key, value)
    }
    if (!('page' in patch)) next.delete('page')
    setParams(next)
  }

  const total = collections.data?.total ?? null
  const limit = collections.data?.limit ?? 24
  const lastPage =
    total === null ? 1 : Math.max(1, Math.ceil(total / limit))

  return (
    <div className={styles.screen}>
      <ScreenHeader
        title="Коллекции"
        subtitle={total === null ? 'каталог' : `${total} в каталоге`}
      />

      <SearchBox
        initial={query}
        onSearch={(value) => apply({ query: value })}
      />

      <CategoryChips
        tree={categories.data ?? []}
        active={category}
        onPick={(slug) => apply({ category: slug })}
      />

      {collections.isLoading && <span className="label">Загрузка…</span>}

      {collections.error !== null && (
        <div className={styles.failure}>{collections.error}</div>
      )}

      {collections.data !== null &&
        (collections.data.items.length === 0 ? (
          <Panel>
            <EmptyState text="По этим условиям ничего не нашлось" />
          </Panel>
        ) : (
          <>
            <div className={styles.grid}>
              {collections.data.items.map((collection) => (
                <CollectionCard
                  key={collection.id}
                  collection={collection}
                />
              ))}
            </div>
            <Pager
              page={page}
              lastPage={lastPage}
              onGo={(next) => apply({ page: String(next) })}
            />
          </>
        ))}
    </div>
  )
}

/**
 * Поиск уходит по кнопке или Enter, а не на каждый символ: запрос идёт
 * в таблицу на одиннадцать тысяч строк, и набранное слово означало бы
 * столько запросов, сколько в нём букв.
 */
function SearchBox({
  initial,
  onSearch
}: {
  initial: string
  onSearch: (value: string) => void
}): ReactNode {
  const [text, setText] = useState(initial)

  return (
    <div className={styles.search}>
      <div className={styles.searchField}>
        <TextField
          label="Поиск по названию"
          value={text}
          onChange={setText}
          placeholder="Чемпионат мира"
          onSubmit={() => onSearch(text)}
        />
      </div>
      <Button kind="primary" onClick={() => onSearch(text)}>
        Найти
      </Button>
      {initial.length > 0 && (
        <Button
          onClick={() => {
            setText('')
            onSearch('')
          }}
        >
          Сбросить
        </Button>
      )}
    </div>
  )
}

/**
 * Два ряда: корневые разделы и подкатегории раскрытого. Всё дерево
 * сразу не показываем — в нём сто двадцать две категории.
 */
function CategoryChips({
  tree,
  active,
  onPick
}: {
  tree: CategoryNode[]
  active: string | null
  onPick: (slug: string | null) => void
}): ReactNode {
  if (tree.length === 0) return null

  // Раскрыт тот раздел, который выбран сам либо содержит выбранную
  // подкатегорию.
  const openRoot =
    tree.find(
      (root) =>
        root.slug === active ||
        root.children.some((child) => child.slug === active)
    ) ?? null

  return (
    <Section gap={8}>
      <div className={styles.chips}>
        <Chip
          title="Все"
          active={active === null}
          onClick={() => onPick(null)}
        />
        {tree.map((root) => (
          <Chip
            key={root.slug}
            title={root.title}
            active={openRoot?.slug === root.slug}
            onClick={() => onPick(root.slug)}
          />
        ))}
      </div>

      {openRoot !== null && openRoot.children.length > 0 && (
        <div className={`${styles.chips} ${styles.chipsChildren}`}>
          {openRoot.children.map((child) => (
            <Chip
              key={child.slug}
              title={`${child.title} · ${child.collectionsCount}`}
              active={child.slug === active}
              // Повторный клик по выбранной подкатегории поднимает к
              // разделу целиком — иначе из неё некуда вернуться.
              onClick={() =>
                onPick(child.slug === active ? openRoot.slug : child.slug)
              }
            />
          ))}
        </div>
      )}
    </Section>
  )
}

function Chip({
  title,
  active,
  onClick
}: {
  title: string
  active: boolean
  onClick: () => void
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[styles.chip, active ? styles.chipActive : '']
        .filter(Boolean)
        .join(' ')}
    >
      {title}
    </button>
  )
}

function CollectionCard({
  collection
}: {
  collection: Collection
}): ReactNode {
  return (
    <Link to={`/collections/${collection.slug}`} className={styles.card}>
      <span className={styles.cardTitle}>{collection.title}</span>
      <span className="label">{collection.category}</span>
      <div className={styles.cardMeta}>
        <span className="muted">
          {collection.year ?? '—'}
          {collection.publisher !== null && ` · ${collection.publisher}`}
        </span>
        <span
          className={styles.cardCount}
          style={{ marginLeft: 'auto' }}
          title="Предметов в наборе"
        >
          {collection.itemsTotal}
        </span>
      </div>
    </Link>
  )
}

function Pager({
  page,
  lastPage,
  onGo
}: {
  page: number
  lastPage: number
  onGo: (page: number) => void
}): ReactNode {
  if (lastPage <= 1) return null
  return (
    <div className={styles.pager}>
      <Button disabled={page <= 1} onClick={() => onGo(page - 1)}>
        Назад
      </Button>
      <span className="label">
        {page} из {lastPage}
      </span>
      <Button disabled={page >= lastPage} onClick={() => onGo(page + 1)}>
        Далее
      </Button>
    </div>
  )
}
